import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, UtensilsCrossed } from "lucide-react";
import { SITE, DISHES, OUTLETS } from "@/data/site";
import { Gonjong, Stamp } from "@/components/Gonjong";

const marqueeItems = [
  "Rendang",
  "Dendeng Batokok Lado Merah",
  "Ayam Pop",
  "Ikan Salai",
  "Telur Barendo",
  "Teh Talua",
  "Es Durian",
  "Nasi Bungkus",
];

const Home = () => (
  <main data-testid="home-page">
    <section className="relative flex min-h-[88vh] items-center overflow-hidden bg-ink">
      <img
        src="/images/hero-hidangan.jpg"
        alt="Aneka hidangan masakan Padang tersaji di etalase rumah makan"
        width="1280"
        height="720"
        fetchpriority="high"
        className="absolute inset-0 h-full w-full object-cover opacity-55"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/25" />
      <div className="relative mx-auto w-full max-w-6xl px-4 py-24 sm:px-6">
        <p className="rise-in text-sm font-bold uppercase tracking-[0.22em] text-kunyit">
          {SITE.name} · {SITE.tagline}
        </p>
        <h1
          data-testid="home-h1"
          className="rise-in rise-in-1 mt-4 max-w-3xl font-display text-4xl font-black leading-[1.05] text-rice sm:text-5xl lg:text-6xl"
        >
          Nasi Padang Payakumbuah,{" "}
          <em className="font-semibold italic text-kunyit">Rasa Asli Minangkabau</em>
        </h1>
        <p className="rise-in rise-in-2 mt-6 max-w-xl text-base leading-relaxed text-rice/85 sm:text-lg">
          Rendang yang pekat, dendeng batokok berlado merah, ayam pop yang
          lembut — dimasak otentik dengan beras Solok pilihan dari Sumatera
          Barat.
        </p>
        <div className="rise-in rise-in-3 mt-9 flex flex-wrap items-center gap-4">
          <Link
            to="/menu/"
            data-testid="hero-cta-menu"
            className="inline-flex items-center gap-2 rounded-full bg-lado px-7 py-3.5 text-base font-bold text-rice transition-transform hover:-translate-y-0.5"
          >
            <UtensilsCrossed className="h-5 w-5" aria-hidden="true" />
            Lihat Menu
          </Link>
          <a
            href={SITE.wa.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-wa"
            className="inline-flex items-center gap-2 rounded-full border-2 border-rice/70 px-7 py-3.5 text-base font-bold text-rice transition-colors hover:border-kunyit hover:text-kunyit"
          >
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
            Pesan Sekarang
          </a>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 text-rice">
        <Gonjong />
      </div>
    </section>

    <div className="overflow-hidden border-b border-ink/10 bg-kunyit py-3" aria-hidden="true">
      <div className="marquee-track">
        {[0, 1].map((dup) => (
          <div key={dup} className="flex shrink-0 items-center">
            {marqueeItems.map((item) => (
              <span
                key={`${dup}-${item}`}
                className="flex items-center whitespace-nowrap font-display text-lg font-bold text-ink"
              >
                <span className="px-5">{item}</span>
                <span className="text-lado">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>

    <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
      <div className="grid items-center gap-12 md:grid-cols-2">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-ijo">
            Cerita Kami
          </p>
          <h2 className="mt-3 font-display text-3xl font-black leading-tight text-ink sm:text-4xl">
            Dari tantangan sang Wakil Gubernur, lahirlah rumah makan ini
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink/80">
            {SITE.name} lahir dari tantangan Wakil Gubernur Sumatera Barat,
            Audy Joinaldy, kepada Arief Muhammad untuk membuka bisnis kuliner
            khas Minangkabau. Sejak diresmikan pada 24 Juli 2022, kami menjaga
            satu prinsip: rasa harus tetap otentik.
          </p>
          <p className="mt-4 text-base leading-relaxed text-ink/80">
            Karena itu beras yang kami pakai adalah beras Solok — salah satu
            beras terbaik dari Sumatera Barat — dimasak berdampingan dengan
            santan, cabai, dan rempah pilihan.
          </p>
          <Link
            to="/tentang/"
            data-testid="home-about-link"
            className="mt-7 inline-flex items-center gap-2 font-bold text-lado underline-offset-4 hover:underline"
          >
            Baca cerita lengkapnya →
          </Link>
        </div>
        <div className="relative">
          <div className="absolute -left-3 -top-3 h-full w-full rounded-2xl border-2 border-kunyit" aria-hidden="true" />
          <img
            src="/images/nasi-padang.jpg"
            alt="Sepiring nasi Padang lengkap dengan lauk khas Minang"
            width="1280"
            height="720"
            loading="lazy"
            className="relative w-full rounded-2xl object-cover shadow-xl"
          />
          <Stamp className="absolute -right-4 -top-6 bg-rice/90" />
        </div>
      </div>
    </section>

    <section className="bg-secondary/60 py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-ijo">
            Hidangan Andalan
          </p>
          <h2 className="mt-3 font-display text-3xl font-black leading-tight sm:text-4xl">
            Yang selalu dicari di meja hidang
          </h2>
        </div>
        <div className="mt-14 space-y-16">
          {DISHES.map((dish, i) => (
            <article
              key={dish.name}
              data-testid={`dish-row-${i}`}
              className={`grid items-center gap-8 md:grid-cols-2 ${
                i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={dish.img}
                  alt={dish.alt}
                  width={dish.w}
                  height={dish.h}
                  loading="lazy"
                  className="h-64 w-full object-cover transition-transform duration-500 hover:scale-105 sm:h-80"
                />
              </div>
              <div>
                <p className="font-display text-5xl font-black text-lado/25">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-2 font-display text-2xl font-bold sm:text-3xl">
                  {dish.name}
                </h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-ink/75">
                  {dish.desc}
                </p>
              </div>
            </article>
          ))}
        </div>
        <div className="mt-14 text-center">
          <Link
            to="/menu/"
            data-testid="home-menu-link"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-bold text-rice transition-transform hover:-translate-y-0.5"
          >
            Jelajahi Menu Lengkap
          </Link>
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden bg-lado-deep text-rice">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-20 sm:px-6 md:grid-cols-2 md:py-24">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-kunyit">
            Nasi Box & Acara
          </p>
          <h2 className="mt-3 font-display text-3xl font-black leading-tight sm:text-4xl">
            Lamak-nya masakan Minang, sampai ke acara Anda
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-rice/85">
            Kami melayani pemesanan nasi box dan catering untuk rapat, syukuran,
            hingga acara keluarga besar. Sampaikan kebutuhan Anda — tim kami
            siap membantu.
          </p>
          <a
            href={SITE.wa.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-catering-wa"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-kunyit px-7 py-3.5 text-base font-bold text-ink transition-transform hover:-translate-y-0.5"
          >
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
            Pesan Nasi Box via WhatsApp
          </a>
        </div>
        <img
          src="/images/nasi-bungkus.jpg"
          alt="Nasi Padang bungkus daun pisang siap dibawa"
          width="1280"
          height="2278"
          loading="lazy"
          className="mx-auto h-80 w-full max-w-sm rounded-2xl object-cover shadow-2xl md:h-[28rem]"
        />
      </div>
      <div className="absolute bottom-0 left-0 right-0 text-rice opacity-100">
        <Gonjong className="text-rice" />
      </div>
    </section>

    <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
      <div className="grid gap-10 md:grid-cols-[1.2fr_1fr] md:items-center">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-ijo">
            Outlet Kami
          </p>
          <h2 className="mt-3 font-display text-3xl font-black leading-tight sm:text-4xl">
            Hadir di berbagai kota
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-ink/80">
            Dari Kemang sampai Semarang — temukan outlet {SITE.name} terdekat
            dari Anda.
          </p>
          <Link
            to="/lokasi/"
            data-testid="home-outlets-link"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-lado px-7 py-3.5 text-base font-bold text-rice transition-transform hover:-translate-y-0.5"
          >
            Lihat Semua Lokasi
          </Link>
        </div>
        <ul className="grid grid-cols-2 gap-3" data-testid="home-outlet-chips">
          {OUTLETS.slice(0, 8).map((o) => (
            <li
              key={o.name}
              className="rounded-xl border border-ink/15 bg-card px-4 py-3 text-sm font-semibold text-ink"
            >
              {o.city}
            </li>
          ))}
        </ul>
      </div>
    </section>
  </main>
);

export default Home;
